// Words the lab screens share (docs/plans/phase1-lab.md §8). One name per state everywhere.
import { labFlag, type LabInterpretation, type LabReportStatus } from '@wodi/domain';
import type { ClinicalNotificationView, LabWorklistState } from '@wodi/shared';
import type { TagState } from '@wodi/ui';

export const REPORT_STATUS_LABELS: Record<LabReportStatus, string> = {
  in_progress: 'In progress',
  awaiting_validation: 'Awaiting validation',
  technically_validated: 'Validated by the lab, awaiting clinical validation',
  final: 'Released',
  superseded: 'Replaced by a correction',
  entered_in_error: 'Entered in error',
};

export const REPORT_STATUS_TAGS: Record<LabReportStatus, TagState> = {
  in_progress: 'active',
  awaiting_validation: 'pending',
  technically_validated: 'pending',
  final: 'done',
  superseded: 'neutral',
  entered_in_error: 'neutral',
};

export const WORKLIST_TABS: readonly { state: LabWorklistState; label: string; key: string }[] = [
  { state: 'to_collect', label: 'To collect', key: '1' },
  { state: 'in_progress', label: 'In progress', key: '2' },
  { state: 'to_validate', label: 'To validate', key: '3' },
  { state: 'released', label: 'Released today', key: '4' },
];

export const PRIORITY_LABELS = { routine: 'Routine', urgent: 'Urgent' } as const;

export const SAMPLE_STATUS_LABELS = {
  collected: 'Collected',
  received: 'Received in the lab',
  rejected: 'Rejected',
  entered_in_error: 'Entered in error',
} as const;

export { formatWaiting } from '../format/waiting.ts';

/** The flag column: the marker, and the word screen readers and the key use. */
export function flagOf(interpretation: LabInterpretation) {
  return labFlag(interpretation);
}

/** One sentence for the notification strip, naming the patient by name and number. */
export function notificationSentence(n: ClinicalNotificationView): string {
  const who = `${n.patient.displayName}, ${n.patient.patientNumber}`;
  switch (n.kind) {
    case 'lab_critical_result':
      return n.asEscalation
        ? `Critical lab result not acknowledged for ${who} (${n.testName}). Phone the clinician.`
        : `Critical lab result for ${who} (${n.testName}). Open and acknowledge.`;
    case 'lab_report_corrected':
      return `Lab report corrected for ${who} (${n.testName}).`;
    case 'lab_report_entered_in_error':
      return `Lab report entered in error for ${who} (${n.testName}).`;
  }
}
